import React, { useEffect, useMemo, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  createProviderCart,
  removeListingFromCart,
  selectCartEntries,
} from '../../ducks/cart.duck';
import { userDisplayNameAsString } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { LISTING_STATE_CLOSED, LISTING_STATE_PUBLISHED } from '../../util/types';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { hasCompleteCustomsData, hasCompleteParcelData } from '../../util/shipping';

import {
  H1,
  IconInfo,
  IconArrowHead,
  IconCart2,
  IconSpinner,
  LayoutSingleColumn,
  NamedLink,
  Page,
  PrimaryButton,
} from '../../components';

import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import { loadCartListings } from './CartPage.duck';
import CartCard from './components/CartCard/CartCard';
import css from './CartPage.module.css';

const staleReason = (row, intl) => {
  const listing = row.listing;

  if (!listing?.id) {
    return intl.formatMessage({ id: 'CartPage.stale.listingMissing' });
  }

  if (
    listing.attributes.state !== LISTING_STATE_PUBLISHED ||
    listing.attributes.state === LISTING_STATE_CLOSED
  ) {
    return intl.formatMessage({ id: 'CartPage.stale.listingUnavailable' });
  }

  const currentStock = listing.currentStock?.attributes?.quantity;
  if (typeof currentStock === 'number' && currentStock < row.quantity) {
    return intl.formatMessage({ id: 'CartPage.stale.stockChanged' });
  }

  const listingPrice = listing.attributes.price;
  if (
    listingPrice?.amount !== row.priceSnapshot?.amount ||
    listingPrice?.currency !== row.priceSnapshot?.currency
  ) {
    return intl.formatMessage({ id: 'CartPage.stale.priceChanged' });
  }

  const publicData = listing.attributes.publicData || {};
  if (!publicData.shippingEnabled) {
    return intl.formatMessage({ id: 'CartPage.stale.deliveryUnavailable' });
  }

  if (!hasCompleteParcelData(publicData) || !hasCompleteCustomsData(publicData)) {
    return intl.formatMessage({ id: 'CartPage.stale.shippingIncomplete' });
  }

  return null;
};

const buildCheckout = ({ entries, routeConfiguration, history, dispatch, currentUser }) => {
  if (!entries.length) {
    return;
  }

  const providerCart = createProviderCart(entries);
  const anchorListing = entries[0].listing;
  const { setInitialValues } = findRouteByRouteName('CheckoutPage', routeConfiguration);
  const transactionListingRefs = entries.map(entry => ({
    id: entry.listing.id,
    type: entry.listing.type,
  }));
  const initialValues = {
    listing: anchorListing,
    orderData: {
      deliveryMethod: 'shipping',
      providerCart,
      fromCart: true,
    },
    confirmPaymentError: null,
    transactionListingRefs,
  };

  dispatch(setInitialValues(initialValues, !currentUser?.id));

  history.push(
    createResourceLocatorString(
      'CheckoutPage',
      routeConfiguration,
      {
        id: anchorListing.id.uuid,
        slug: createSlug(anchorListing.attributes.title),
      },
      {}
    )
  );
};

const SeparateCheckoutNotice = () => {
  return (
    <section className={css.notice}>
      <div className={css.noticeIconBadge}>
        <IconInfo rootClassName={css.noticeIcon} />
      </div>
      <div className={css.noticeContent}>
        <p className={css.noticeTitle}>
          <FormattedMessage id="CartPage.separateCheckoutTitle" />
        </p>
        <p className={css.noticeText}>
          <FormattedMessage id="CartPage.separateCheckoutBody" />
        </p>
      </div>
    </section>
  );
};

/**
 * Cart page.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const CartPageComponent = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const intl = useIntl();
  const [hasMounted, setHasMounted] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const {
    cartPageLoaded,
    cartEntries,
    dispatch,
    fetchCartListingsError,
    fetchCartListingsInProgress,
    currentUser,
    onRemoveListing,
    onRetryFetch,
  } = props;

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const hasCartEntries = cartEntries.length > 0;
  const hasUnresolvedCartListings = cartEntries.some(entry => !entry.listing);
  const showInitialLoadingState =
    !hasMounted ||
    !cartPageLoaded ||
    (hasCartEntries &&
      fetchCartListingsInProgress &&
      !fetchCartListingsError &&
      hasUnresolvedCartListings);
  const showFetchErrorState =
    cartPageLoaded && hasCartEntries && !fetchCartListingsInProgress && !!fetchCartListingsError;

  const groupedCart = useMemo(() => {
    const groups = cartEntries.reduce((acc, entry) => {
      const key = entry.sellerId;
      const nextRow = {
        ...entry,
        key: `${entry.sellerId}:${entry.listingId}`,
        staleReason: staleReason(entry, intl),
      };
      nextRow.isStale = !!nextRow.staleReason;

      if (!acc[key]) {
        acc[key] = {
          key,
          sellerId: entry.sellerId,
          seller: entry.listing?.author || null,
          sellerName: userDisplayNameAsString(
            entry.listing?.author,
            intl.formatMessage({ id: 'CartPage.unknownSeller' })
          ),
          rows: [],
        };
      }

      acc[key].rows.push(nextRow);
      return acc;
    }, {});

    return Object.values(groups);
  }, [cartEntries, intl]);

  const onSelectRow = (row, checked) => {
    setSelectedKeys(current => {
      const next = new Set(current);
      if (checked) {
        next.add(row.key);
      } else {
        next.delete(row.key);
      }
      return next;
    });
  };

  const removeRow = row => {
    setSelectedKeys(current => {
      const next = new Set(current);
      next.delete(row.key);
      return next;
    });
    onRemoveListing({
      sellerId: row.sellerId,
      deliveryMethod: row.deliveryMethod,
      listingId: row.listingId,
    });
  };

  const checkoutEntries = rows => {
    buildCheckout({ entries: rows, routeConfiguration, history, dispatch, currentUser });
  };

  const layoutConfig = config.layout.listingImage;

  return (
    <Page title={intl.formatMessage({ id: 'CartPage.title' })}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.root}>
          <header className={css.header}>
            <H1 as="h1" className={css.heading}>
              <FormattedMessage
                id="CartPage.headingWithCount"
                values={{ count: groupedCart.length }}
                defaultMessage={`Your Cart (${groupedCart.length})`}
              />
            </H1>
          </header>

          {showInitialLoadingState ? (
            <section className={css.loadingPanel}>
              <IconSpinner rootClassName={css.loadingSpinner} />
              <p className={css.loadingCopy}>
                <FormattedMessage id="CartPage.loadingListings" />
              </p>
            </section>
          ) : showFetchErrorState ? (
            <section className={css.errorPanel}>
              <p className={css.errorCopy}>
                <FormattedMessage id="CartPage.fetchListingsFailed" />
              </p>
              <PrimaryButton
                onClick={() => onRetryFetch(config)}
                rootClassName={css.retryButton}
                type="button"
              >
                <FormattedMessage id="CartPage.retryFetchListings" />
              </PrimaryButton>
            </section>
          ) : groupedCart.length === 0 ? (
            <>
              <section className={css.emptyState}>
                <div className={css.emptyIllustration}>
                  <div className={css.emptyIllustrationRing}>
                    <IconCart2 className={css.emptyIcon} />
                  </div>
                </div>

                <div className={css.emptyContent}>
                  <h2 className={css.emptyTitle}>
                    <FormattedMessage id="CartPage.empty" />
                  </h2>
                  <p className={css.emptyDescription}>
                    <FormattedMessage id="CartPage.emptyDescription" />
                  </p>
                  <NamedLink className={css.emptyCta} name="SearchPage">
                    <span className={css.emptyCtaLabel}>
                      <FormattedMessage id="CartPage.exploreSellers" />
                    </span>
                    <IconArrowHead
                      direction="right"
                      size="small"
                      rootClassName={css.emptyCtaArrow}
                    />
                  </NamedLink>
                </div>
              </section>

              <SeparateCheckoutNotice />
            </>
          ) : (
            <>
              <div className={css.cards}>
                {groupedCart.map(group => {
                  const selectedRows = group.rows.filter(row => selectedKeys.has(row.key));
                  const validRows = group.rows.filter(row => !row.isStale);
                  const rowsToCheckout = selectedRows.length
                    ? selectedRows.filter(row => !row.isStale)
                    : validRows;

                  return (
                    <CartCard
                      group={group}
                      key={group.key}
                      layoutListingImageConfig={layoutConfig}
                      onCheckoutGroup={() => checkoutEntries(rowsToCheckout)}
                      onCheckoutRow={row => checkoutEntries([row])}
                      onRemove={removeRow}
                      onSelectRow={onSelectRow}
                      selectedKeys={selectedKeys}
                    />
                  );
                })}
              </div>

              {groupedCart.length > 1 ? <SeparateCheckoutNotice /> : null}
            </>
          )}
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const {
    listingRefs = [],
    fetchCartListingsError = null,
    fetchCartListingsInProgress = false,
    cartPageLoaded = false,
  } = state.CartPage;
  const listings = getMarketplaceEntities(state, listingRefs);
  const listingMap = listings.reduce((acc, listing) => {
    acc[listing.id.uuid] = listing;
    return acc;
  }, {});

  return {
    currentUser: state.user?.currentUser,
    cartEntries: selectCartEntries(state).map(entry => ({
      ...entry,
      listing: listingMap[entry.listingId] || null,
    })),
    cartPageLoaded,
    fetchCartListingsError,
    fetchCartListingsInProgress,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    dispatch,
    onRemoveListing: params => dispatch(removeListingFromCart(params)),
    onRetryFetch: config => dispatch(loadCartListings(config)),
  };
};

const CartPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(CartPageComponent);

export default CartPage;
